import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import {
  chinaImportState,
  filteredShopNShipLinksState,
  shopNShipDetailsState,
  userState
} from '../../../../../recoil/atoms';
import * as yup from 'yup';
import { useFormik } from 'formik';
import Input from '../../../../../components/new-dashboard/custom-input';
import { Alert, Grid } from '@mui/material';
import NewShopNShipStepsLayout from '../../../../../components/Layouts/shop-n-ship-steps-layout';
import SubmitButton from '../../../../../components/submit-button';
import { InfoDarkIcon } from '../../../../../constants/asset-contants';
import AddressGuidePrompt from '../../../../../components/modals/address-guide-prompt';

const PackageNameCustomLabelMessage = ({
  tagName,
  country,
  isChinaImport,
  isExpress
}) => (
  <Alert
    severity={'info'}
    sx={{
      marginTop: '10px',
      fontSize: '1.2rem',
      fontFamily: 'General Sans',
      backgroundColor: '#F8FBFF',
      color: '#22428F',
      border: 'none',
      '& .MuiAlert-icon': {
        fontSize: '1.6rem',
        marginRight: '5px',
        marginTop: '2px',
        color: '#22428F'
      }
    }}
  >
    {isChinaImport ? (
      <>
        The sender’s name should bear ‘ [identifier tag] [first name] [last
        name]
        <b> e.g 邓涛topship - Jane Doe.</b> Please add the identifier tag
        <b>-邓涛topship</b> to your name, so your package can be easily
        identified when it lands at the China warehouse.
      </>
    ) : isExpress ? (
      <>
        On Express Shop & Ship service, we require that you enter ONLY this tag
        “Topship Africa / LOS 80205” as your name when shipping to our hub. Your
        package will be identified via the tracking number you provide
      </>
    ) : (
      <>
        Please include <b>‘{tagName}’</b> for <b>{country}</b> shipping, in the
        sender’s name when sending to any of the addresses above e.g{' '}
        <b>Tayo Aluko - {tagName}</b>{' '}
      </>
    )}
  </Alert>
);

const ShopnshipPackageInformation = () => {
  const [shopNshipDetails, setSaveShopNshipDetails] = useRecoilState(
    shopNShipDetailsState
  );
  const history = useHistory();
  const [isChinaImport] = useRecoilState(chinaImportState);

  const [filteredLinks] = useRecoilState(filteredShopNShipLinksState);
  const [user] = useRecoilState(userState);

  const currentIndex = filteredLinks.findIndex(
    link => link.link === history.location.pathname
  );
  const [showAddressGuide, setShowAddressGuide] = useState(false);

  const countryTags = useMemo(() => {
    return {
      USA: 'UAC-mujeq',
      UK: 'UAC-ytroe',
      CANADA: 'UAC-rkipz',
      CHINA: '邓涛topship'
    };
  }, []);

  const packageRegex = new RegExp(
    `${
      shopNshipDetails?.shippingFrom?.includes('UK')
        ? countryTags?.UK
        : shopNshipDetails?.shippingFrom?.includes('USA')
        ? countryTags?.USA
        : shopNshipDetails?.shippingFrom?.includes('CANADA')
        ? countryTags?.CANADA
        : countryTags?.CHINA
    }`,
    'g'
  );
  const standardValidation = yup
    .string()
    .required('Package Name is required')
    .matches(packageRegex, {
      message: `Please include "- ${
        countryTags[shopNshipDetails?.shippingFrom?.split(' ')[0]]
      }" `
    });
  const expressLabel = 'TOPSHIP AFRICA/LOS 80205';

  const expressValidation = yup.string().required('Package Name is required');
  const isExpressOption = shopNshipDetails?.pricingTier === 'Express';
  const tagName = useMemo(() => {
    return countryTags[shopNshipDetails?.shippingFrom?.split(' ')[0]] || '';
  }, [countryTags, shopNshipDetails?.shippingFrom]);

  const countryName = shopNshipDetails?.shippingFrom?.split(' ')[0];
  const validationSchema = yup.object().shape({
    packageName: isExpressOption ? expressValidation : standardValidation,
    deliveryName: yup.string().required('Please enter a delivery name'),
    deliveryAddress: yup.string().required('Please enter a delivery address')
  });

  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    setFieldValue
  } = useFormik({
    validationSchema,
    enableReinitialize: true,
    initialValues: {
      packageName: `${user?.fullName} -${tagName}` || '',
      deliveryName: user?.fullName || '',
      deliveryAddress: ''
    },

    onSubmit: values => {
      setSaveShopNshipDetails({ ...shopNshipDetails, ...values });
      history.push(filteredLinks[currentIndex + 1].link);
    }
  });
  useEffect(() => {
    const keys = Object.keys(shopNshipDetails);
    const _values = Object.values(shopNshipDetails);
    const formKeys = Object.keys(values);
    _values.forEach((value, index) => {
      const formKey = formKeys.find(key => keys[index] === key);
      if (value && shopNshipDetails[keys[index]] === value && formKey) {
        setFieldValue(keys[index], value);
      }
    });
    if (shopNshipDetails.pricingTier === 'Express') {
      setFieldValue('packageName', expressLabel);
    } else {
      setFieldValue('packageName', `${user?.fullName} -${tagName}`);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <NewShopNShipStepsLayout mobileTitle='Package Information'>
        <div className='new-shop-n-ship__modal__body'>
          <div className='new-shop-n-ship__modal__body__wrap'>
            <div className='new-shop-n-ship__modal__body__content'>
              <form
                onSubmit={handleSubmit}
                className='new-shop-n-ship__modal__body__form'
              >
                <Grid container rowSpacing='16px' columnSpacing='16px'>
                  <Input
                    name='packageName'
                    value={values.packageName}
                    error={touched.packageName && Boolean(errors.packageName)}
                    helperText={touched.packageName && errors.packageName}
                    customLabelMessage={
                      <PackageNameCustomLabelMessage
                        tagName={tagName}
                        country={countryName}
                        isChinaImport={isChinaImport}
                        isExpress={isExpressOption}
                      />
                    }
                    onChange={handleChange}
                    customLabel='Name on package'
                    type='text'
                    placeholder={
                      isChinaImport
                        ? '邓涛topship - Jane Doe'
                        : isExpressOption
                        ? expressLabel
                        : `e.g: Garrick Braide - ${
                            countryTags[
                              shopNshipDetails?.shippingFrom?.split(' ')[0]
                            ] || ''
                          }`
                    }
                    containerClassName='new-shop-n-ship__modal__body__form__item__input'
                    fullWidth
                    disabled={shopNshipDetails.pricingTier === 'Express'}
                  />

                  <Input
                    name='deliveryName'
                    value={values.deliveryName}
                    error={touched.deliveryName && Boolean(errors.deliveryName)}
                    helperText={touched.deliveryName && errors.deliveryName}
                    onChange={handleChange}
                    customLabel="Receiver's Full Name"
                    type='text'
                    placeholder='Uchenna Topship'
                    containerClassName='new-shop-n-ship__modal__body__form__item__input'
                    fullWidth
                  />
                  <Input
                    name='deliveryAddress'
                    value={values.deliveryAddress}
                    error={
                      touched.deliveryAddress && Boolean(errors.deliveryAddress)
                    }
                    helperText={
                      touched.deliveryAddress && errors.deliveryAddress
                    }
                    onChange={handleChange}
                    customLabel={
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <p className='mb-0'>Receiver's Address</p>
                        <img
                          title='Click to see address guide'
                          onClick={() => setShowAddressGuide(true)}
                          style={{
                            width: '20px',
                            paddingLeft: '6px',
                            cursor: 'pointer'
                          }}
                          src={InfoDarkIcon}
                          alt=''
                        />
                      </div>
                    }
                    customLabelMessage='Enter the address in Nigeria where you want this item to be delivered'
                    type='text'
                    placeholder='Oando Petrol Station, Iweka Road'
                    containerClassName='new-shop-n-ship__modal__body__form__item__input'
                    fullWidth
                  />
                </Grid>
                {isChinaImport ? (
                  <Alert
                    severity={'info'}
                    sx={{
                      marginTop: '10px',
                      fontSize: '1.5rem',
                      fontWeight: 'medium',
                      fontFamily: 'General Sans',
                      backgroundColor: '#E1F9FF',
                      color: '#416EB2',
                      border: 'none',
                      '& .MuiAlert-icon': {
                        fontSize: '1.6rem',
                        marginRight: '5px',
                        marginTop: '2px',
                        color: '#416EB2'
                      }
                    }}
                  >
                    Please note that doorstep delivery would come at an extra
                    cost to the receiver
                  </Alert>
                ) : null}
                <div className='new-shop-n-ship__modal__body__form__item__buttons'>
                  <div
                    className='new-shop-n-ship__modal__body__form__item__button'
                    onClick={() =>
                      history.push(filteredLinks[currentIndex - 1].link)
                    }
                  >
                    Go Back
                  </div>
                  <SubmitButton text='Continue' />
                </div>
              </form>
            </div>
          </div>
        </div>
      </NewShopNShipStepsLayout>
      <AddressGuidePrompt
        show={showAddressGuide}
        setShow={setShowAddressGuide}
      />
    </>
  );
};

export default ShopnshipPackageInformation;
